import type { PortServiceFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/utils";
import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/statusKeys";
import { approveByMDShipToShipSchema } from "../schemas/approveByMDShipToShipSchema";
import { closeShipToShipSchema } from "../schemas/closeShipToShipSchema";
import { rejectionSchema } from "../schemas/rejectionSchema";
import { startShipToShipSchema } from "../schemas/startShipToShipSchema";

export const SHIP_TO_SHIP_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["MD"],
    canReject: ["MD"],
    nextStatus: STATUS_KEYS.MD_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    statusesList: {
      [STATUS_KEYS.MD_APPROVED]: {
        label: "Approved by Marine Department",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesShipToShip",
        extraFields: ["externalNotes", "internalNotes"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesShipToShip",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortServicesShipToShip",
        extraFields: ["ats", "etc"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    canCancel: [
      {
        userRoles: ["marine-operator", "marine-supervisor"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortServicesShipToShip",
        extraFields: ["atc", "billOfLading", "cargoType", "operationType", "actualQuantity"],
      },
    },
  },
};

export const validateShipToShipExtraFields = ({
  newStatusOption,
  notifyOption,
  internalNotes,
  externalNotes,
  rejectionReason,
  flowAts,
  flowEtc,
  flowAtc,
  billOfLading,
  cargoType,
  operationType,
  actualQuantity,
}: {
  newStatusOption: string;
  notifyOption: string[];
  internalNotes: string;
  externalNotes: string;
  rejectionReason: string;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  billOfLading: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
  cargoType: string | null;
  operationType: string | null;
  actualQuantity?: number;
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.MD_APPROVED:
      return approveByMDShipToShipSchema().safeParse({
        notificationOption: notifyOption,
        internalNotes,
        externalNotes,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startShipToShipSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeShipToShipSchema(flowAts).safeParse({
        atc: flowAtc,
        notificationOption: notifyOption,
        billOfLading,
        cargoType,
        operationType,
        actualQuantity,
      });

    default:
      return null;
  }
};

export const isShipToShip = (category: string) => {
  return category === "Ship to Ship";
};

export const isShipToShipType = (category: string, type: string) => {
  return isShipToShip(category) && type === "Ship to Ship";
};
